import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Divider } from '@mui/material';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    OutlinedInput,
    Chip,
    Button
} from '@mui/material';
import {GroupAdd } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import { httpGet, httpPost, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import { getFilter, setFilter } from '../Common/Utilities';
import { useGridApiRef } from '@mui/x-data-grid';

export default function UserGroupsManagement(props) {

    const [filterText, setFilterText] = useState(getFilter(['SystemSetup', 'UserGroupsManagement', 'Rules', 'searchText']) || '');
    const [userInfo] = useState(() => {
        try {
            const raw = localStorage.getItem('userInfo');
            return raw ? JSON.parse(raw) : null;   
        } catch (e) {
            return null;
        }
    });
    const apiGridRef = useGridApiRef();
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [openNewGroup, setOpenNewGroup] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [newGroupName, setNewGroupName] = useState('');
    const [copyFromGroup, setCopyFromGroup] = useState('');
    const [errors, setErrors] = useState({ groupName: false, copyFrom: false });
    const [allGroups, setAllGroups] = useState([]);
    const [roles, setRoles] = useState([]);
    const [userRoleMap, setUserRoleMap] = useState({});
    const [rows, setRows] = useState([]);
    const [selectOpen, setSelectOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [newUserGroupIsAdmin, setNewUserGroupIsAdmin] = useState(false);
    const [allowRoutingSetup, setAllowRoutingSetup] = useState(false);
    const [newUserGroupAllowRoutingSetup, setNewUserGroupAllowRoutingSetup] = useState(false);
    const [allowApprovalRoutingSetup, setAllowApprovalRoutingSetup] = useState(false);
    const [newUserGroupAllowApprovalRoutingSetup, setNewUserGroupAllowApprovalRoutingSetup] = useState(false);
    const [positionCodeNameMap, setPositionCodeNameMap] = useState({});
    const [isNewGroupMode, setIsNewGroupMode] = useState(false);
    const [companyApprovalConfig, setCompanyApprovalConfig] = useState([]);
    const [dirty, setDirty] = useState(false);


    useEffect(() => {

        if (!userInfo) return;

        props.inProgress(true);
       
        const gridRef = apiGridRef?.current;
        if (gridRef && typeof gridRef.setQuickFilterValues === 'function') {
            gridRef.setQuickFilterValues([filterText]);
        }

        Promise.all([
            httpGet('/api/userroles/'),
            httpGet('/api/positioncategory/'),
            httpGet('/api/company/' + userInfo.companyCode) 
        ])
            .then(([userRolesRes, positionRes, companyRes]) => {
                const data = userRolesRes.data;
                const groupNames = data.map((r) => r.userRoleName);
                const map = {};
                data.forEach((role) => {
                    map[role.userRoleName] = role;
                });
                setAllGroups(groupNames);
                setUserRoleMap(map);
                if (groupNames.length > 0) {
                    setSelectedGroup(groupNames[0]);
                }

                const codeToName = {};
                const names = positionRes.data.map((r) => {
                    codeToName[r.positionCategoryCode] = r.positionCategoryName;
                    return r.positionCategoryName;
                });
                setRoles(names);
                setPositionCodeNameMap(codeToName);

            
                setCompanyApprovalConfig(companyRes.data.companyApprovalConfigurations || []);
            })
            .catch(() => toast.error('An error occurred while fetching the records'))
            .finally(() => props.inProgress(false));
    }, []);

    useEffect(() => {
        if (!selectedGroup || !userRoleMap[selectedGroup]) return;
        const group = userRoleMap[selectedGroup];

        setIsAdmin(group.isAdmin);
        setAllowRoutingSetup(group.isAllowRoutingSetup);
        setAllowApprovalRoutingSetup(group.isAllowApprovalRoutingSetup);
        setSelectedRoles(
            (group.apadUserRolePositionCategories || []).map((r) => positionCodeNameMap[r.positionCategoryCode]).filter(Boolean)
        );

        const flatPermissions = {};
        (group.userRolesInvoiceRightsList || []).forEach((model) => {
            const config = companyApprovalConfig.find(cfg => cfg.modelDefId === model.modelDefId);
            const maxLevel = config?.routingNumberOfApprovalLevels || 0;

            Object.keys(model).forEach((key) => {
                if (key.endsWith('Email')) return; 
                if ((model[key] !== 'Y' && model[key] !== 'N') || typeof model[key] !== 'string') return;

                const match = key.match(/^allowInvoiceLevel(\d)Approval$/);
                if (match && parseInt(match[1]) > maxLevel) return;

                const label = getPermissionLabel(key);
                const category = model.modelDefId === 1 ? 'Media' : model.modelDefId === 2 ? 'Production' : 'Broadcast';
                const id = `${category}_${label}`;

                const emailKey = key + 'Email';
                const hasEmail = emailKey in model;

                flatPermissions[id] = {
                    enabled: model[key] === 'Y',
                    email: hasEmail ? model[emailKey] === 'Y' : false,
                    hasEmail,
                };
            });

        });

        setPermissions(flatPermissions);
    }, [selectedGroup, userRoleMap, positionCodeNameMap]);

    useEffect(() => {
        const groupedRows = [];
        const categoryMap = {};

        Object.entries(permissions).forEach(([key, val]) => {

            const [category, label] = key.split('_');
            if (!categoryMap[category]) categoryMap[category] = [];

            categoryMap[category].push({
                id: key,
                category,
                description: label,
                enabled: val.enabled,
                email: val.email,
                hasEmail: val.hasEmail,
            });
        });

        Object.entries(categoryMap).forEach(([category, rows], index) => {
            groupedRows.push({
                id: `header_${category}_${index}_${Date.now() + Math.random()}`,
                isGroupHeader: true,
                category,
            });
            groupedRows.push(...rows);
        });

        setRows(
            groupedRows.filter(
                (row) => !row.description || row.description.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [permissions, searchTerm]);

    const getPermissionLabel = (key) => {
        if (key === 'allowInvoiceAutoRouting') {
            return 'Discrepant Routing';
        }

        const label = key
            .replace(/^allow/, '')
            .replace(/([A-Z])/g, ' $1')
            .trim();

        if (label === 'change Company') {
            return 'Change Company';
        }

        return label;
    };

    const handleDialogClose = () => {
        setOpenNewGroup(false);
        setIsNewGroupMode(false);
        setErrors({ groupName: false, copyFrom: false });
        setNewGroupName('');
        setCopyFromGroup('');
        setDirty(false);

    };

    const handlePermissionChange = (id, field, value) => {
        setDirty(true);
        setPermissions((prev) => {
            const existing = prev[id] || { enabled: false, email: false, hasEmail: false };
            const next = {
                ...existing,
                [field]: value,
                hasEmail: existing.hasEmail || field === 'email',
            };

            if (field === 'enabled' && !value) next.email = false;

            return { ...prev, [id]: next };
        });
    };

    const handleDeleteUserGroup = () => {
        if (!groupToDelete) return;

        props.inProgress(true);
        const { companyCode, userRoleId } = groupToDelete;

        httpDelete(`/api/userroles/${companyCode}/${userRoleId}`)
            .then((res) => {
                const { errorCode, errorMessage } = res.data;

                if (errorCode === -1) {
                    toast.error(errorMessage || 'Unexpected error');
                    setOpenDeleteDialog(false);
                    return; 
                }
                toast.success('User Group deleted successfully');

                return httpGet('/api/userroles/').then((res) => {
                    const data = res.data;
                    const map = {};
                    data.forEach((role) => {
                        map[role.userRoleName] = role;
                    });
                    setUserRoleMap(map);
                    const updatedGroups = data.map((r) => r.userRoleName);
                    setAllGroups(updatedGroups);
                    setSelectedGroup(updatedGroups[0] || '');
                    setGroupToDelete(null);
                    setOpenDeleteDialog(false);
                });
            })
            .catch((err) => {
                toast.error(`Delete Failed: ${err.response?.data || 'Unexpected error'}`);
            })
            .finally(() => {
                props.inProgress(false);
            });
    };

    const handleSave = () => {
        try {
            
            if (isNewGroupMode) {
                const trimmedGroupName = newGroupName.trim();
                const nameMissing = !trimmedGroupName;
                const nameExists = allGroups.some(
                    (name) => name.toLowerCase() === trimmedGroupName.toLowerCase()
                );
                const copyMissing = !copyFromGroup;

                if (nameMissing || nameExists || copyMissing) {
                    setErrors({
                        groupName: nameMissing || nameExists,
                        copyFrom: copyMissing,
                    });
                    return;
                }
            }

            const sourceGroup = isNewGroupMode
                ? userRoleMap[copyFromGroup]
                : userRoleMap[selectedGroup];

            if (!sourceGroup) return;

            const wasNewGroup = isNewGroupMode;
            props.inProgress(true);

           
            const updatedGroup = structuredClone(sourceGroup);

            if (isNewGroupMode) {
               
                const trimmedName = newGroupName.trim();

                updatedGroup.userRoleName = trimmedName;
                updatedGroup.userRoleId = 0;

               
                updatedGroup.isAdmin = newUserGroupIsAdmin;
                updatedGroup.isAdminRole = newUserGroupIsAdmin ? 'Y' : 'N';

                updatedGroup.isAllowRoutingSetup = newUserGroupAllowRoutingSetup;
                updatedGroup.allowRoutingSetup = newUserGroupAllowRoutingSetup ? 'Y' : 'N';

                updatedGroup.isAllowApprovalRoutingSetup = newUserGroupAllowApprovalRoutingSetup;
                updatedGroup.allowApprovalRoutingSetup =
                    newUserGroupAllowApprovalRoutingSetup ? 'Y' : 'N';

                
                if (Array.isArray(updatedGroup.apadUserRolePositionCategories)) {
                    updatedGroup.apadUserRolePositionCategories =
                        updatedGroup.apadUserRolePositionCategories.map((item) => ({
                            ...item,
                            userRoleId: 0,
                        }));
                }

                
            } else {
               
                const adminVal = isAdmin;
                const routingVal = allowRoutingSetup;
                const approvalVal = allowApprovalRoutingSetup;

                updatedGroup.isAdmin = adminVal;
                updatedGroup.isAdminRole = adminVal ? 'Y' : 'N';
                updatedGroup.isAllowRoutingSetup = routingVal;
                updatedGroup.allowRoutingSetup = routingVal ? 'Y' : 'N';
                updatedGroup.isAllowApprovalRoutingSetup = approvalVal;
                updatedGroup.allowApprovalRoutingSetup = approvalVal ? 'Y' : 'N';

                
                const nameToCodeMap = Object.entries(positionCodeNameMap).reduce(
                    (acc, [code, name]) => {
                        acc[name] = parseInt(code, 10);
                        return acc;
                    },
                    {}
                );

                updatedGroup.apadUserRolePositionCategories = selectedRoles.map((role) => ({
                    companyCode: updatedGroup.companyCode,
                    userRoleId: updatedGroup.userRoleId,
                    positionCategoryCode: nameToCodeMap[role],
                    createId: userInfo.id,
                    createDate: new Date().toISOString(),
                    modifyId: null,
                    modifyDate: null,
                    apadUserRoles: null,
                    apamPositionCategory: null,
                }));

                
                Object.entries(permissions).forEach(([key, val]) => {
                    const [category, label] = key.split('_');
                    const modelDefId =
                        category === 'Media' ? 1 : category === 'Production' ? 2 : 3;

                    const model = (updatedGroup.userRolesInvoiceRightsList || []).find(
                        (m) => m.modelDefId === modelDefId
                    );
                    if (!model) return;

                    const config = companyApprovalConfig.find(
                        (cfg) => cfg.modelDefId === modelDefId
                    );
                    const maxLevel = config?.routingNumberOfApprovalLevels || 0;

                    const baseKey = Object.keys(model).find((k) => {
                        if (
                            (model[k] !== 'Y' && model[k] !== 'N') ||
                            typeof model[k] !== 'string'
                        )
                            return false;

                        const match = k.match(/^allowInvoiceLevel(\d)Approval$/);
                        if (match && parseInt(match[1], 10) > maxLevel) return false;

                        const fieldLabel = getPermissionLabel(k);

                        return fieldLabel === label;
                    });

                    if (!baseKey) return;

                    model[baseKey] = val.enabled ? 'Y' : 'N';

                    const emailKey = baseKey + 'Email';
                    if (val.hasEmail && emailKey in model) {
                        model[emailKey] = val.email ? 'Y' : 'N';
                    }
                });
            }

            httpPost('/api/userroles/', updatedGroup)
                .then(() => httpGet('/api/userroles/'))
                .then((res) => {
                    const data = res.data;
                    const map = {};
                    data.forEach((role) => {
                        map[role.userRoleName] = role;
                    });
                    setUserRoleMap(map);
                    setAllGroups(data.map((r) => r.userRoleName));

                    if (isNewGroupMode) {
                        setSelectedGroup(newGroupName.trim());
                    }

                    setOpenNewGroup(false);
                    setIsNewGroupMode(false);
                    setNewGroupName('');
                    setCopyFromGroup('');
                })
                .catch(() => {
                    toast.error('An error occurred while saving the user group');
                })
                .finally(() => {
                    props.inProgress(false);
                    setDirty(false);
                    toast.success(
                        wasNewGroup
                            ? 'New User Group created successfully'
                            : 'Updated successfully'
                    );
                });
        } catch (err) {
            props.inProgress(false);
            toast.error('Something went wrong while processing your request.');
        }
    };


    const handleReset = () => {
        if (!selectedGroup || !userRoleMap[selectedGroup]) return;

        const group = userRoleMap[selectedGroup];

        setIsAdmin(group.isAdmin);
        setAllowRoutingSetup(group.isAllowRoutingSetup);
        setAllowApprovalRoutingSetup(group.isAllowApprovalRoutingSetup);
        setSelectedRoles(
            (group.apadUserRolePositionCategories || [])
                .map((r) => positionCodeNameMap[r.positionCategoryCode])
                .filter(Boolean)
        );

        const flatPermissions = {};
        (group.userRolesInvoiceRightsList || []).forEach((model) => {
            const modelDefId = model.modelDefId;
            const config = companyApprovalConfig.find(cfg => cfg.modelDefId === modelDefId);
            const maxLevel = config?.routingNumberOfApprovalLevels || 0;

            Object.keys(model).forEach((key) => {
                if (key.endsWith('Email')) return; 
                if ((model[key] === 'Y' || model[key] === 'N') && typeof model[key] === 'string') {
                    const match = key.match(/^allowInvoiceLevel(\d)Approval$/);
                    if (match) {
                        const level = parseInt(match[1]);
                        if (level > maxLevel) return;
                    }

                    const label = getPermissionLabel(key);

                    const category = modelDefId === 1 ? 'Media' : modelDefId === 2 ? 'Production' : 'Broadcast';
                    const isApprovalLevel = !!match;
                    const hasEmail = isApprovalLevel || key === 'allowInvoiceAutoRouting';
                    const emailKey = key + 'Email';

                    flatPermissions[`${category}_${label}`] = {
                        enabled: model[key] === 'Y',
                        email: hasEmail && model[emailKey] === 'Y',
                        hasEmail: !!hasEmail,
                    };
                }

            });
        });

        setPermissions(flatPermissions);
        setDirty(false);
    };

    const columns = [
        {
            field: 'description',
            headerName: 'Rule Description',
            flex: 1,
            minWidth: 400,
            maxWidth: 1000,
            sortable: false,
            renderCell: (params) => params.row.isGroupHeader
                ? <Typography sx={{ fontWeight: 500, fontSize: '15px' }}>{params.row.category}</Typography>
                : <Typography sx={{ fontSize: '14px', pl: 2 }}>{params.row.description}</Typography>,
        },
        {
            field: 'enabled',
            headerName: 'Enable',
            width: 100,
            sortable: false,
            renderCell: (params) => !params.row.isGroupHeader && (
                <Checkbox
                    checked={params.value}
                    onChange={(e) => handlePermissionChange(params.id, 'enabled', e.target.checked)}
                />
            ),
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 100,
            sortable: false,
            renderCell: (params) => {
                const perm = permissions[params.id];
                if (!perm?.hasEmail || params.row.isGroupHeader) return null;

                const isEmailAllowed = perm.enabled;

                return (
                    <Checkbox
                        checked={perm.email}
                        disabled={!isEmailAllowed}
                        onChange={(e) => handlePermissionChange(params.id, 'email', e.target.checked)}
                    />
                );
            },

        },
    ];



    return (
        <Box p={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" mt={3} mb={5}>
                    User Groups Management
                </Typography>
                <button
                    onClick={() => {
                        setOpenNewGroup(true);
                        setIsNewGroupMode(true);
                        setNewGroupName('');
                        setCopyFromGroup('');
                        setErrors({ groupName: false, copyFrom: false });
                        setNewUserGroupIsAdmin(false);
                        setNewUserGroupAllowRoutingSetup(false);
                        setNewUserGroupAllowApprovalRoutingSetup(false);
                        
                    }}
                    style={{
                        backgroundColor: '#e0e0e0',
                        color: '#333',
                        padding: '6px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 'bold',
                        transition: 'background 0.2s ease'
                    }}
                >
                    <GroupAdd />
                    Add New Group
                </button>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                <Box display="flex" flexDirection="column" justifyContent="flex-start" sx={{ minWidth: 200 }}>
                    <FormControl fullWidth>
                        <InputLabel>User Group</InputLabel>
                        <Select
                            value={selectedGroup}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                            input={<OutlinedInput label="User Group" />}
                        >
                            {allGroups.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {g}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ width: 250 }}>
                    <FormControl fullWidth>
                        <InputLabel>Assigned Role(s)</InputLabel>
                        <Select
                            multiple
                            open={selectOpen}
                            onOpen={() => setSelectOpen(true)}
                            onClose={() => setSelectOpen(false)}
                            value={selectedRoles}
                            onChange={(e) => {
                                const value = e.target.value;
                                const newItems = Array.isArray(value) ? value : [value];
                                setSelectedRoles([...new Set([...newItems])]);
                                setDirty(true);
                                setSelectOpen(false);
                            }}
                            input={<OutlinedInput label="Assigned Role(s)" />}
                            renderValue={(selected) => {
                                if (selected.length === 0) return 'No Assigned Role(s)';
                                else if (selected.length === 1) return 'Single';
                                else return 'Multiple';
                            }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                        >
                            {roles.map((role) => (
                                <MenuItem
                                    key={role}
                                    value={role}
                                    disabled={selectedRoles.includes(role)}
                                >
                                    {role}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    
                </Box>
                <Box sx={{ mt: 1, ml:10 }}>

                    <Box display="flex" gap={5} >
                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ flex: 1 }}>Admin</Typography>
                            <Checkbox
                                checked={isAdmin}
                                onChange={(e) => {
                                    setIsAdmin(e.target.checked);
                                    setDirty(true);
                                }}
                                size="small"
                            />
                        </Box>

                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ flex: 1 }}>Allow Routing Setup</Typography>
                            <Checkbox
                                checked={allowRoutingSetup}
                                onChange={(e) => {
                                    setAllowRoutingSetup(e.target.checked);
                                    setDirty(true);
                                }}
                                size="small"
                            />
                        </Box>

                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ flex: 1 }}>Allow Approval Routing Setup</Typography>
                            <Checkbox
                                checked={allowApprovalRoutingSetup}
                                onChange={(e) => {
                                    setAllowApprovalRoutingSetup(e.target.checked);
                                    setDirty(true);
                                }}
                                size="small"
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box sx={{ ml: 'calc(200px + 16px)', flexGrow: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedRoles.map((role) => (
                            <Chip
                                key={role}
                                label={role}
                                onDelete={() => {
                                    setDirty(true);
                                    setSelectedRoles((prev) => prev.filter((r) => r !== role));
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>

            <EnhancedDataGrid
                rows={rows}
                apiRef={apiGridRef}
                columns={columns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                hidePagination={true}
                editMode="row"
                autoHeight
                setFilterText={(text) => {
                    const trimmed = text?.trim() || '';
                    setFilterText(trimmed);
                    setFilter(['SystemSetup', 'UserGroupsManagement', 'Rules', 'searchText'], trimmed);
                }}
                filterText={filterText}
                sx={{
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontSize: '16px',

                    },
                }}
            />


            <Box sx={{
                            position: 'fixed',
                            bottom: 20,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1000,
                            display: 'flex',
                            gap: 2
            }}>
                <button className="btn-grey" onClick={handleReset} disabled={!dirty} >Reset</button>
                <button
                    className="btn-grey"
                    style={{ backgroundColor: '#d32f2f', color: '#ffffff', border: 'none' }}
                    disabled={!selectedGroup}
                    onClick={() => {
                        const group = userRoleMap[selectedGroup];
                        if (group) {
                            setGroupToDelete(group);
                            setOpenDeleteDialog(true);
                        }
                    }}
                >
                    Delete
                </button>


                <button className="btn-blue" onClick={handleSave} disabled={!dirty} >Save</button>

            </Box>
            <Dialog
                open={openDeleteDialog}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Record
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete user group '{groupToDelete?.userRoleName}' ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteUserGroup} color="primary">
                        Yes
                    </Button>
                    <Button autoFocus onClick={() => setOpenDeleteDialog(false)} color="primary">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
           
            <Dialog
                open={openNewGroup}
                onClose={() => { }}
                disableEscapeKeyDown
                hideBackdrop={false}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        pt: 3,
                        pb:2,
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ccc',
                        borderRadius: 2,
                        minHeight: 250,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: 16, px: 3, pt: 0, pb: 1 }}>
                    Create New User Group
                </DialogTitle>

               
                <Divider sx={{ width: '100%' }} />

                <DialogContent sx={{ pt: 4, px: 5, pb: 1 }}>
                    <Box display="flex" gap={2} mb={2}>
                        <Box flex={1}>
                            <Typography variant="body2" mb={0.5}>Group Name:</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={newGroupName}
                                onChange={(e) => {
                                    if (e.target.value.length <= 64) {
                                        setNewGroupName(e.target.value);
                                        if (errors.groupName) setErrors(prev => ({ ...prev, groupName: false }));
                                    }
                                }}
                                error={errors.groupName}
                                helperText={
                                    errors.groupName
                                        ? allGroups.some(name => name.toLowerCase() === newGroupName.trim().toLowerCase())
                                            ? "Group name already exists"
                                            : "Group name is required"
                                        : ""
                                }
                                FormHelperTextProps={{
                                    sx: {
                                        textAlign: 'left',
                                        marginLeft: 0,
                                    }
                                }}
                                placeholder="Enter new group name"
                                inputProps={{ maxLength: 64 }}
                                sx={{
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#888',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>

                        <Box flex={1}>
                            <Typography variant="body2" mb={0.5}>Copy Settings From:</Typography>
                            <FormControl fullWidth size="small" error={errors.copyFrom}>
                                <Select
                                    value={copyFromGroup}
                                    onChange={(e) => {
                                        const selectedCopyFrom = e.target.value;
                                        setCopyFromGroup(selectedCopyFrom);
                                        if (errors.copyFrom) setErrors(prev => ({ ...prev, copyFrom: false }));

                                        const source = userRoleMap[selectedCopyFrom];
                                        if (source) {
                                            setNewUserGroupAllowRoutingSetup(!!source.isAllowRoutingSetup);
                                            setNewUserGroupAllowApprovalRoutingSetup(!!source.isAllowApprovalRoutingSetup);
                                        }
                                    }}
                                    input={<OutlinedInput />}
                                    displayEmpty
                                    renderValue={(selected) =>
                                        selected === ''
                                            ? <span style={{ color: '#888' }}>Select User group to copy Settings</span>
                                            : selected
                                    }
                                >
                                    {allGroups.map((group) => (
                                        <MenuItem key={group} value={group}>
                                            {group}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.copyFrom && (
                                    <Typography variant="caption" color="error" mt={0.5}>
                                        Please select a user group to copy settings
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                   
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">Admin</Typography>
                        <Checkbox
                            checked={newUserGroupIsAdmin}
                            onChange={(e) => setNewUserGroupIsAdmin(e.target.checked)}
                            size="small"
                        />
                    </Box>
                </DialogContent>

               
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <button
                        onClick={handleDialogClose}
                        className="btn-grey"
                        style={{ padding: '8px 24px', cursor: 'pointer' }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="btn-blue"
                        style={{ padding: '8px 24px', cursor: 'pointer' }}
                    >
                        Save
                    </button>
                </DialogActions>
            </Dialog>

        </Box>

    );
}

